import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

interface StackTraceViewerProps {
  stack?: string | null;
}

const StackTraceViewer: React.FC<StackTraceViewerProps> = ({ stack }) => {
  const { t } = useTranslation();
  const safeParse = (input?: string | null): any => {
    if (!input) return null;
    let data: any = input;
    try {
      for (let i = 0; i < 3; i++) {
        if (typeof data === "string") {
          try {
            data = JSON.parse(data);
          } catch {
            break;
          }
        }
      }
      return data;
    } catch {
      return input;
    }
  };

  const syntaxHighlight = (json: any): string => {
    if (typeof json !== "string") {
      json = JSON.stringify(json, null, 2);
    }
    json = json
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
    return json.replace(
      /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g,
      (match: any) => {
        let cls = "number";
        if (/^"/.test(match)) {
          if (/:$/.test(match)) cls = "key";
          else cls = "string";
        } else if (/true|false/.test(match)) cls = "boolean";
        else if (/null/.test(match)) cls = "null";
        return `<span class="${cls}">${match}</span>`;
      }
    );
  };

  const formatted = useMemo(() => {
    const parsed = safeParse(stack);
    return parsed ? syntaxHighlight(parsed) : t("noStackTrace");
  }, [stack, t]);

  return (
    <div
      style={{
        background: "#1e1e1e",
        color: "#eee",
        padding: "1rem",
        borderRadius: "10px",
        fontFamily: "monospace",
        fontSize: "0.9rem",
      }}
    >
      <div style={{ color: "#ff5555", fontWeight: "bold", marginBottom: 8 }}>
        {t("stackTrace")}
      </div>
      <pre
        style={{
          margin: 0,
          whiteSpace: "pre-wrap", // ✅ allow wrapping
          wordBreak: "break-word", // ✅ break long words if needed
          overflowWrap: "anywhere", // ✅ modern browsers
        }}
        dangerouslySetInnerHTML={{ __html: formatted }}
      />
      <style>
        {`
          .key { color: #61dafb; }
          .string { color: #a6e22e; }
          .number { color: #fd971f; }
          .boolean { color: #ff79c6; }
          .null { color: #999; }
        `}
      </style>
    </div>
  );
};

export default StackTraceViewer;
